/* global SignaturePad, emailautocomplete */

import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "onlineFields",
    "uploadFields",
    "canvas",
    "signaturePadInput",
    "uploadInput",
    "uploadPreviewContainer",
    "uploadPreview",
    "uploadFileName",
  ]

  connect() {
    this.isLegacyFields = this.data.has("legacy-fields")
    this.missingSignatureMessage = this.data.get("missing-signature-message")
    this.signaturePad = null
    this.lastWidth = null
    this.lastHeight = null

    if (this.hasCanvasTarget) {
      this.initSignaturePad()
    }

    this.initEmailAutocomplete()

    this.submitReviewedForm = this.submitReviewedForm.bind(this)
    document.addEventListener("consent-review:confirmed", this.submitReviewedForm)
  }

  disconnect() {
    if (this.resizeCanvas) {
      window.removeEventListener("resize", this.resizeCanvas)
    }

    document.removeEventListener("consent-review:confirmed", this.submitReviewedForm)
  }

  submitReviewedForm() {
    if (this.isLegacyFields) {
      this.disableInactiveTab()
    }

    this.element.closest("form").submit()
  }

  disableInactiveTab() {
    const inactiveTab = [this.onlineFieldsTarget, this.uploadFieldsTarget].find(
      target => !target.classList.contains("active"),
    )

    if (inactiveTab) this.disableTargetFields(inactiveTab)
  }

  initSignaturePad() {
    this.signaturePad = new SignaturePad(this.canvasTarget, { backgroundColor: "#f2f2f2" })
    window.signaturePad = this.signaturePad

    // A signature that came back from the review round-trip (Submit -> review ->
    // Edit) arrives only as a rasterized data URL in the hidden field. Keep it so
    // every resize re-restores from this original image instead of re-capturing an
    // already-scaled canvas -- re-capturing is what made the signature grow on
    // each round-trip and vanish on resize.
    this.restoreDataUrl = null
    if (this.hasSignaturePadInputTarget && this.signaturePadInputTarget.dataset.restore) {
      const restoreValue = this.signaturePadInputTarget.dataset.restore
      if (restoreValue.startsWith("data:")) this.restoreDataUrl = restoreValue
    }

    this.resizeCanvas = this.resizeCanvas.bind(this)
    window.addEventListener("resize", this.resizeCanvas)

    // Size the drawing buffer to the *settled* layout. Measuring at connect() can
    // run before the CSS-driven canvas height (clamp/vw) resolves, leaving the
    // buffer under-sized -- the captured signature is then low-res and gets scaled
    // up (looks bigger/blurry) when restored. The rAF pass re-sizes once layout
    // has settled.
    this.sizeCanvasToDisplay()
    window.requestAnimationFrame(() => this.sizeCanvasToDisplay())
  }

  resizeCanvas() {
    this.sizeCanvasToDisplay()
  }

  // Match the canvas drawing buffer to its displayed CSS size, then restore the
  // signature. Three cases:
  //  - fresh strokes drawn this session -> replay the vector points (crisp and
  //    position-stable at any size);
  //  - a signature restored from the review round-trip, untouched -> re-restore the
  //    original image (raster carries no vector data);
  //  - a restored signature the photographer then drew on top of -> flatten the
  //    whole canvas to a fresh image so neither the original nor the new strokes
  //    are lost (a vector-only redraw would drop the restored image).
  sizeCanvasToDisplay() {
    if (!this.hasCanvasTarget) return

    const canvas = this.canvasTarget
    const width = canvas.offsetWidth
    const height = canvas.offsetHeight
    if (!width || !height) return
    if (width === this.lastWidth && height === this.lastHeight) return

    const hasContent = this.signaturePad && !this.signaturePad.isEmpty()
    const vectorData = hasContent ? this.signaturePad.toData() : null
    const editingRestoredSignature =
      hasContent && this.restoreDataUrl && vectorData && vectorData.length
    const flattened = editingRestoredSignature ? this.signaturePad.toDataURL() : null

    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    canvas.width = width * ratio
    canvas.height = height * ratio
    canvas.getContext("2d").scale(ratio, ratio)
    this.signaturePad.clear()

    if (flattened) {
      this.restoreDataUrl = flattened
      this.signaturePad.fromDataURL(flattened)
    } else if (vectorData && vectorData.length) {
      this.signaturePad.fromData(vectorData)
    } else if (this.restoreDataUrl) {
      this.signaturePad.fromDataURL(this.restoreDataUrl)
    }

    this.lastWidth = width
    this.lastHeight = height
  }

  initEmailAutocomplete() {
    emailautocomplete(".email-target-field", {
      domains: ["gmail.com", "yahoo.com", "hotmail.com", "icloud.com", "outlook.com"],
      suggClass: "custom-classname",
    })
  }

  addFamilyContact() {
    setTimeout(() => this.initEmailAutocomplete(), 200)
  }

  clear() {
    if (this.signaturePad) this.signaturePad.clear()
    if (this.hasSignaturePadInputTarget) this.signaturePadInputTarget.value = ""
    // Drop any round-tripped signature baseline; otherwise a resize would redraw
    // the signature the photographer just cleared.
    this.restoreDataUrl = null
  }

  setSignature(event) {
    if (this.isSignaturePadEmpty() && (this.isLegacyFields || !this.isValidFileUploaded())) {
      alert(this.missingSignatureMessage)
      event.preventDefault()
      return
    }

    if (this.signaturePad && this.hasSignaturePadInputTarget) {
      this.signaturePadInputTarget.value = this.signaturePad.toDataURL()
    }
  }

  isValidFileUploaded() {
    return this.hasUploadInputTarget && this.uploadInputTarget.value !== ""
  }

  isSignaturePadEmpty() {
    return this.signaturePad && this.signaturePad.isEmpty()
  }

  disableInactiveTabFields(event) {
    if (this.isLegacyFields) {
      ;[this.onlineFieldsTarget, this.uploadFieldsTarget].forEach(target => {
        if (!target.contains(event.currentTarget)) {
          this.disableTargetFields(target)
        }
      })
    }
  }

  disableTargetFields(target) {
    target.querySelectorAll("input, select, textarea").forEach(el => {
      el.disabled = true
    })
  }

  validateAndPreviewFile(event) {
    const input = event.currentTarget

    if (this.isFileValid(input)) {
      this.previewFile(input.files[0])
    } else {
      alert(
        `Sorry, ${
          input.value
        } is an invalid extension, the only valid extension to upload a signature is: .png, .jpg, .jpeg, .pdf`,
      )
      input.value = ""
    }
  }

  isFileValid(input) {
    const validExtensions = [".png", ".jpg", ".jpeg", ".pdf"]
    const fileName = input.value

    if (fileName.length === 0) return false

    return validExtensions.some(
      ext => fileName.slice(-ext.length).toLowerCase() === ext.toLowerCase(),
    )
  }

  previewFile(file) {
    this.uploadFileNameTarget.textContent = file.name
    this.uploadPreviewContainerTarget.style.display = "inline-block"

    if (file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onload = e => {
        this.uploadPreviewTarget.src = e.target.result
        this.uploadPreviewTarget.style.display = "block"
      }
      reader.readAsDataURL(file)
    } else {
      // PDF — show filename only
      this.uploadPreviewTarget.style.display = "none"
    }
  }

  syncCheckbox(event) {
    const input = event.currentTarget
    const name = input.name
    const checked = input.checked

    this.element.querySelectorAll(`input[type=checkbox][name="${name}"]`).forEach(checkbox => {
      checkbox.checked = checked
    })
  }

  triggerFileInput() {
    this.uploadInputTarget.click()
  }
}
